// src/components/layout/TenantAdminLayout.jsx
import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useTenant } from '../../context/TenantContext'
import { useAuth } from '../../context/AuthContext'

const NAV = [
  { to: '/tenant/dashboard',  label: 'Dashboard',    icon: '◉' },
  { to: '/tenant/branding',   label: 'Branding',     icon: '🎨' },
  { to: '/tenant/domains',    label: 'Domains',      icon: '🌐' },
  { to: '/tenant/settings',   label: 'Settings',     icon: '⚙' },
  { to: '/tenant/team',       label: 'Team',         icon: '👥' },
  { to: '/tenant/billing',    label: 'Billing',      icon: '💳' },
  { divider: true },
  { to: '/tenant/suppliers',  label: 'Suppliers',    icon: '🏭' },
  { to: '/tenant/products',   label: 'Products',     icon: '📦' },
  { to: '/tenant/orders',     label: 'Orders',       icon: '📋' },
  { to: '/tenant/analytics',  label: 'Analytics',    icon: '📊' },
]

export default function TenantAdminLayout() {
  const { tenant } = useTenant()
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)

  const primary = tenant.primary_color || '#FF3D7F'

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F8F9FA', fontFamily: 'DM Sans, sans-serif' }}>
      {/* Sidebar */}
      <aside style={{
        width: collapsed ? 64 : 240, flexShrink: 0, background: '#fff',
        borderRight: '1px solid #F0F0F0', display: 'flex', flexDirection: 'column',
        transition: 'width 0.25s', overflow: 'hidden', position: 'sticky', top: 0, height: '100vh',
      }}>
        {/* Logo area */}
        <div style={{ padding: collapsed ? '20px 12px' : '20px 20px', borderBottom: '1px solid #F0F0F0', display: 'flex', alignItems: 'center', gap: 10 }}>
          {tenant.logo_url
            ? <img src={tenant.logo_url} alt={tenant.brand_name} style={{ height: 32, objectFit: 'contain' }} />
            : <div style={{ width: 32, height: 32, borderRadius: 8, background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '0.85rem', flexShrink: 0 }}>
                {(tenant.brand_name || 'T')[0].toUpperCase()}
              </div>
          }
          {!collapsed && (
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1A1A2E', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 160 }}>{tenant.brand_name}</div>
              <div style={{ fontSize: '0.65rem', color: '#9CA3AF', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{tenant.plan} Plan</div>
            </div>
          )}
        </div>

        {/* Trial banner */}
        {!collapsed && tenant.is_trial && (
          <div style={{ margin: '12px 12px 0', background: '#FFF7ED', border: '1px solid #FCD34D', borderRadius: 8, padding: '8px 10px' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#D97706' }}>Trial Mode</div>
            <div style={{ fontSize: '0.68rem', color: '#92400E', marginTop: 2 }}>Ends {new Date(tenant.trial_ends_at).toLocaleDateString()}</div>
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 0', overflowY: 'auto' }}>
          {NAV.map((item, i) => {
            if (item.divider) return (
              <div key={i} style={{ margin: '8px 16px', borderTop: '1px solid #F0F0F0' }} />
            )
            return (
              <NavLink
                key={item.to} to={item.to}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: collapsed ? '10px 16px' : '10px 16px',
                  margin: '2px 8px', borderRadius: 8, textDecoration: 'none',
                  background: isActive ? `${primary}15` : 'transparent',
                  color: isActive ? primary : '#374151',
                  fontWeight: isActive ? 700 : 500, fontSize: '0.85rem',
                  transition: 'all 0.15s',
                })}
              >
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>{item.icon}</span>
                {!collapsed && <span style={{ whiteSpace: 'nowrap' }}>{item.label}</span>}
              </NavLink>
            )
          })}
        </nav>

        {/* Footer */}
        <div style={{ padding: collapsed ? '12px 8px' : '12px 16px', borderTop: '1px solid #F0F0F0' }}>
          {!collapsed && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1A1A2E', truncate: true }}>{user?.name}</div>
              <div style={{ fontSize: '0.68rem', color: '#9CA3AF' }}>{user?.email}</div>
            </div>
          )}
          <button
            onClick={() => logout().then(() => navigate('/login'))}
            style={{ width: '100%', padding: '6px 10px', borderRadius: 8, border: '1px solid #F0F0F0', background: '#fff', cursor: 'pointer', fontSize: '0.78rem', color: '#6B7280', textAlign: collapsed ? 'center' : 'left' }}
          >
            {collapsed ? '↩' : '↩ Sign out'}
          </button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(c => !c)}
          style={{ position: 'absolute', top: 24, right: -12, width: 24, height: 24, borderRadius: '50%', background: '#fff', border: '1px solid #E5E7EB', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}
        >
          {collapsed ? '›' : '‹'}
        </button>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, padding: '32px', overflowY: 'auto', minWidth: 0 }}>
        {/* Top bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: primary }} />
            <span style={{ fontSize: '0.75rem', color: '#9CA3AF', fontWeight: 600 }}>
              {tenant.slug}.fabrinza.com
            </span>
          </div>
          <a
            href={`https://${tenant.slug}.fabrinza.com`}
            target="_blank" rel="noreferrer"
            style={{ fontSize: '0.78rem', color: primary, fontWeight: 600, textDecoration: 'none', padding: '6px 12px', border: `1px solid ${primary}30`, borderRadius: 8 }}
          >
            View Marketplace ↗
          </a>
        </div>
        <Outlet />
      </main>
    </div>
  )
}
